import { useCallback, useEffect, useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import {
  orcamentos as api,
  type Comparacao,
  type EspecificacaoDeLivro,
  type LinhaDaMemoria,
  type OrcamentoCalculado,
  type VersaoDeTabela,
} from '../api/orcamentos'
import { Aviso } from '../componentes/Aviso'
import { Botao } from '../componentes/Botao'
import { Campo } from '../componentes/Campo'
import { Cartao } from '../componentes/Cartao'
import { Estado } from '../componentes/Estado'
import { formatar } from '../i18n'
import './usuarios.css'
import './simulacao.css'
import { marcaDaRota } from './marcaDaRota'

const FORMATOS = ['12x18', '14,8x21', '16x23', '20x20', '21x28', '21x29,7']

const PAPEIS = [
  'Offset 75g',
  'Offset 90g',
  'Couche fosco/brilho 115g',
  'Couche fosco/brilho 150g',
  'Couche fosco/brilho 170g',
]

const PADRAO: EspecificacaoDeLivro = {
  tiragem: 500,
  paginas: 128,
  formato: '14,8x21',
  papelDoMiolo: 'Offset 75g',
  comOrelhas: false,
  capaFrenteEVerso: false,
  laminacao: null,
  shrink: false,
  outraBrochura: false,
}


/**
 * Simulação de orçamento.
 *
 * É o item que muda o jogo da validação: hoje o Vicente não tem como conferir o
 * cálculo sem pedir um orçamento pelo WhatsApp. Aqui ele monta uma
 * especificação, vê o preço com a memória aberta e compara com o valor que a
 * gráfica de fato praticou.
 *
 * Isso converte a tabela de preços de **bloqueio** em **ciclo de revisão**.
 */
export function Simulacao() {
  const { t } = useTranslation()
  const marca = (marcaDaRota() ?? '')

  const [especificacao, setEspecificacao] = useState<EspecificacaoDeLivro>(PADRAO)
  const [versoes, setVersoes] = useState<VersaoDeTabela[] | null>(null)
  const [versaoId, setVersaoId] = useState<number | null>(null)
  const [resultado, setResultado] = useState<OrcamentoCalculado | Comparacao | null>(null)
  const [praticado, setPraticado] = useState('')
  const [calculando, setCalculando] = useState(false)
  const [falhou, setFalhou] = useState(false)

  useEffect(() => {
    api
      .versoes()
      .then(setVersoes)
      .catch(() => setFalhou(true))
  }, [])

  const alterar = <C extends keyof EspecificacaoDeLivro>(
    campo: C,
    valor: EspecificacaoDeLivro[C],
  ) => setEspecificacao({ ...especificacao, [campo]: valor })

  const calcular = useCallback(
    async (evento?: FormEvent) => {
      evento?.preventDefault()
      setCalculando(true)

      try {
        const valor = Number(praticado.replace(',', '.'))

        // Com valor praticado informado, a comparação; sem ele, a simulação
        // simples. É a mesma tela porque é o mesmo gesto.
        setResultado(
          praticado && valor > 0
            ? await api.comparar(marca, especificacao, versaoId, valor)
            : await api.simular(marca, especificacao, versaoId),
        )
      } catch {
        setFalhou(true)
      } finally {
        setCalculando(false)
      }
    },
    [marca, especificacao, versaoId, praticado],
  )

  if (falhou) return <Estado tipo="erro" />
  if (!versoes) return <Estado tipo="carregando" />

  if (versoes.length === 0)
    return (
      <main className="usuarios">
        <h1 className="usuarios__titulo">{t('simulacao.titulo')}</h1>
        <Estado tipo="vazio" descricao={t('simulacao.semTabela')} />
      </main>
    )

  const comparacao = resultado && 'valorPraticado' in resultado ? resultado : null

  return (
    <main className="usuarios">
      <header className="usuarios__cabecalho">
        <div>
          <h1 className="usuarios__titulo">{t('simulacao.titulo')}</h1>
          <p className="usuarios__descricao">{t('simulacao.descricao')}</p>
        </div>
      </header>

      <div className="simulacao__colunas">
        <Cartao titulo={t('simulacao.especificacao')}>
          <form onSubmit={calcular} noValidate>
            <Campo
              rotulo={t('simulacao.tiragem')}
              type="number"
              value={especificacao.tiragem}
              onChange={(e) => alterar('tiragem', Number(e.target.value))}
              obrigatorio
            />

            <Campo
              rotulo={t('simulacao.paginas')}
              type="number"
              value={especificacao.paginas}
              onChange={(e) => alterar('paginas', Number(e.target.value))}
              obrigatorio
            />

            <label className="simulacao__seletor">
              <span>{t('simulacao.formato')}</span>
              <select
                value={especificacao.formato}
                onChange={(e) => alterar('formato', e.target.value)}
              >
                {FORMATOS.map((f) => (
                  <option key={f} value={f}>
                    {f}
                  </option>
                ))}
              </select>
            </label>

            <label className="simulacao__seletor">
              <span>{t('simulacao.papel')}</span>
              <select
                value={especificacao.papelDoMiolo}
                onChange={(e) => alterar('papelDoMiolo', e.target.value)}
              >
                {PAPEIS.map((p) => (
                  <option key={p} value={p}>
                    {p}
                  </option>
                ))}
              </select>
            </label>

            <label className="simulacao__seletor">
              <span>{t('simulacao.laminacao')}</span>
              <select
                value={especificacao.laminacao ?? ''}
                onChange={(e) => alterar('laminacao', e.target.value || null)}
              >
                <option value="">{t('simulacao.semLaminacao')}</option>
                <option value="Fosca">{t('simulacao.laminacaoFosca')}</option>
                <option value="Soft Touch">{t('simulacao.laminacaoSoft')}</option>
              </select>
            </label>

            {(
              [
                ['comOrelhas', 'simulacao.orelhas'],
                ['capaFrenteEVerso', 'simulacao.capa44'],
                ['shrink', 'simulacao.shrink'],
                ['outraBrochura', 'simulacao.outraBrochura'],
              ] as const
            ).map(([campo, rotulo]) => (
              <label key={campo} className="usuarios__opcao">
                <input
                  type="checkbox"
                  checked={especificacao[campo]}
                  onChange={(e) => alterar(campo, e.target.checked)}
                />
                <span>
                  <strong>{t(rotulo)}</strong>
                </span>
              </label>
            ))}

            <label className="simulacao__seletor">
              <span>{t('simulacao.versao')}</span>
              <select
                value={versaoId ?? ''}
                onChange={(e) => setVersaoId(e.target.value ? Number(e.target.value) : null)}
              >
                {/* Escolher a versão é o que permite comparar antes e depois de
                    uma mudança de preço. */}
                <option value="">{t('simulacao.versaoVigente')}</option>
                {versoes.map((v) => (
                  <option key={v.id} value={v.id}>
                    {t('simulacao.versaoItem', {
                      numero: v.numero,
                      data: formatar.data(v.vigenteDe),
                    })}
                  </option>
                ))}
              </select>
            </label>

            <Campo
              rotulo={t('simulacao.praticado')}
              value={praticado}
              onChange={(e) => setPraticado(e.target.value)}
              ajuda={t('simulacao.praticadoAjuda')}
              inputMode="decimal"
            />

            <div className="usuarios__acoes-formulario">
              <Botao type="submit" variante="primario" carregando={calculando}>
                {t('simulacao.calcular')}
              </Botao>
            </div>
          </form>
        </Cartao>

        <div>
          {resultado === null ? (
            <Cartao titulo={t('simulacao.resultado')}>
              <Estado tipo="vazio" descricao={t('simulacao.aindaNaoCalculou')} />
            </Cartao>
          ) : !resultado.sucesso ? (
            <Cartao titulo={t('simulacao.resultado')}>
              {/* Regra de exceção não é erro: é a resposta certa. Por isso o tom
                  é de alerta, e não de perigo. */}
              <Aviso tom={resultado.escalonar === 'Nenhum' ? 'perigo' : 'alerta'}>
                {resultado.erro}
              </Aviso>

              {resultado.escalonar !== 'Nenhum' && (
                <p className="usuarios__descricao">
                  {t(`simulacao.escalonar.${resultado.escalonar}`, {
                    defaultValue: t('simulacao.escalonar.Generico'),
                  })}
                </p>
              )}
            </Cartao>
          ) : (
            <>
              <Cartao titulo={t('simulacao.resultado')}>
                <dl className="indicadores">
                  <div className="indicador">
                    <dt>{t('simulacao.total')}</dt>
                    <dd className="indicador__valor">{formatar.moeda(resultado.total)}</dd>
                  </div>

                  <div className="indicador">
                    <dt>{t('simulacao.unitario')}</dt>
                    <dd className="indicador__valor">{formatar.moeda(resultado.unitario)}</dd>
                  </div>

                  <div className="indicador">
                    <dt>{t('simulacao.versaoUsada')}</dt>
                    <dd className="indicador__valor">{resultado.versaoDaTabela}</dd>
                  </div>
                </dl>

                {comparacao && <Diferenca comparacao={comparacao} />}
              </Cartao>

              <Cartao titulo={t('simulacao.memoria')}>
                <p className="usuarios__descricao">{t('simulacao.memoriaAjuda')}</p>
                <Memoria linhas={resultado.memoria} />
              </Cartao>
            </>
          )}
        </div>
      </div>
    </main>
  )
}

/**
 * A diferença entre o cálculo e o que a gráfica praticou.
 *
 * Existe porque o comparativo de 12 livros coloridos mostrou subfaturamento
 * mediano de 22%. Estender ao catálogo vai revelar lacunas parecidas — esta é a
 * leitura que as torna visíveis antes do cliente.
 */
function Diferenca({ comparacao }: { comparacao: Comparacao }) {
  const { t } = useTranslation()

  const abaixo = comparacao.diferenca < 0
  const tom = Math.abs(comparacao.percentual) < 5 ? 'sucesso' : abaixo ? 'perigo' : 'alerta'

  return (
    <Aviso
      tom={tom}
      titulo={t(abaixo ? 'simulacao.abaixo' : 'simulacao.acima', {
        percentual: formatar.numero(Math.abs(comparacao.percentual), {
          maximumFractionDigits: 1,
        }),
      })}
    >
      {t('simulacao.diferencaCorpo', {
        calculado: formatar.moeda(comparacao.total),
        praticado: formatar.moeda(comparacao.valorPraticado),
        diferenca: formatar.moeda(Math.abs(comparacao.diferenca)),
      })}
    </Aviso>
  )
}

/**
 * A memória de cálculo, agrupada por etapa.
 *
 * Não é enfeite: é o que sustenta o preço diante do cliente quando ele
 * questiona, e é lendo linha a linha que se descobre o que está fora do modelo.
 */
function Memoria({ linhas }: { linhas: LinhaDaMemoria[] }) {
  const { t } = useTranslation()

  const etapas = [...new Set(linhas.map((l) => l.etapa))]

  return (
    <div
      className="usuarios__tabela-rolagem"
      // A região rolável precisa ser alcançável pelo teclado (WCAG 2.1.1).
      tabIndex={0}
      role="group"
      aria-label={t('simulacao.memoriaLegenda')}
    >
      <table className="usuarios__tabela simulacao__memoria">
        <caption className="usuarios__legenda">{t('simulacao.memoriaLegenda')}</caption>
        <thead>
          <tr>
            <th scope="col">{t('simulacao.etapa')}</th>
            <th scope="col">{t('simulacao.linha')}</th>
            <th scope="col">{t('simulacao.valor')}</th>
          </tr>
        </thead>
        {etapas.map((etapa) => (
          <tbody key={etapa}>
            {linhas
              .filter((l) => l.etapa === etapa)
              .map((linha, i) => (
                <tr key={`${etapa}-${i}`} className={etapa === 'Resultado' ? 'simulacao__final' : undefined}>
                  {i === 0 ? (
                    <th scope="rowgroup" rowSpan={linhas.filter((l) => l.etapa === etapa).length}>
                      {etapa}
                    </th>
                  ) : null}
                  <td>
                    {linha.descricao}
                    {linha.origem && <small className="simulacao__origem">{linha.origem}</small>}
                  </td>
                  <td className="simulacao__numero">
                    {formatar.numero(linha.valor, { maximumFractionDigits: 4 })}
                  </td>
                </tr>
              ))}
          </tbody>
        ))}
      </table>
    </div>
  )
}
